import { PhpArray, PhpFloat, strval, json_encode, type PhpArrayInput, type PhpKeyInput } from '../../php-compat';
export type Row = PhpArrayInput;
export function isArray(v: unknown): v is Row { return v !== null && typeof v === 'object' && !(v instanceof PhpFloat); }
/** Indexing glue for tagged ordered arrays and ordinary compiled JSON. */
export function at(v: unknown, ...path: PhpKeyInput[]): unknown {
    for (const key of path) {
        if (typeof v === 'string') {
            // PHP string offsets address UTF-8 bytes, including negative offsets.
            const index = typeof key === 'string' && !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key) ? NaN : Number(key);
            const bytes = encodeURIComponent(v).replace(/%([\dA-F]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
            const offset = Math.trunc(index);
            v = Number.isFinite(index) ? bytes[offset < 0 ? bytes.length + offset : offset] : undefined;
        } else v = v instanceof PhpArray ? v.get(key) : isArray(v) ? (v as Record<string, unknown>)[String(key)] : undefined;
    }
    return v ?? null;
}
export function rows(v: unknown): Row { if (!isArray(v)) throw new TypeError('PHP array required'); return v; }
export function values(v: unknown): unknown[] { return v instanceof PhpArray ? [...v.values()] : isArray(v) ? Object.values(v) : []; }
export function entries(v: Row): [PhpKeyInput, unknown][] { return v instanceof PhpArray ? [...v] : Object.entries(v); }
export function has(v: Row, key: string): boolean { return v instanceof PhpArray ? v.has(key) : Object.prototype.hasOwnProperty.call(v, key); }
/** The shared encoder has no INVALID_UTF8_SUBSTITUTE flag yet. C3 receives
 * decoded JS strings; repair unpaired UTF-16 before the canonical encoder,
 * preserving associative keys, ordered arrays and partial recursion output. */
export function renderJson(value: unknown): string | false {
    const seen = new Set<object>();
    const valid = (text: string): string => Array.from(text, char => char.length === 1 && /[\uD800-\uDFFF]/.test(char) ? '\uFFFD' : char).join('');
    const copy = (input: unknown): unknown => {
        if (typeof input === 'string') return valid(input);
        if (!isArray(input)) return input;
        if (seen.has(input)) return null;
        seen.add(input);
        const result = new PhpArray(entries(input).map(([key, entry]) => [typeof key === 'string' ? valid(key) : key, copy(entry)]));
        seen.delete(input); return result;
    };
    return json_encode(copy(value), 64 | 256 | 512);
}
/** PHP htmlspecialchars flags used by these renderers (ENT_QUOTES, ENT_COMPAT,
 * ENT_NOQUOTES, ENT_SUBSTITUTE; HTML401 apostrophe entity). UTF-8 JS strings
 * arrive decoded; invalid surrogate code units follow ENT_SUBSTITUTE. */
export function htmlspecialchars(value: unknown, flags = 11): string {
    if (isArray(value)) throw new TypeError('htmlspecialchars(): Argument #1 ($string) must be of type string, array given');
    let s = strval(value);
    if (/[\uD800-\uDFFF]/u.test(s)) {
        let invalid = false;
        s = Array.from(s, c => { if (c.length === 1 && /[\uD800-\uDFFF]/.test(c)) { invalid = true; return '\uFFFD'; } return c; }).join('');
        if (invalid && !(flags & 8)) return '';
    }
    s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (flags & 2) s = s.replace(/"/g, '&quot;');
    if (flags & 1) s = s.replace(/'/g, flags & 48 ? '&apos;' : '&#039;');
    return s;
}
/** HasIdByVariable's static cache belongs to the request environment, never the
 * module. Cache keys and insertion order mirror includes/catalogs/_traits.php. */
export function catalogGetId(className: string, catalog: readonly { variable_name: string; item_id: string }[], name: string, env: { globals: Record<string, unknown> }): string | null {
    const key = className + '::$idCache', existing = at(env.globals[key], name);
    if (existing !== null) return strval(existing);
    const cache = new PhpArray(entries(isArray(env.globals[key]) ? env.globals[key] as Row : []));
    const id = catalog.find(c => c.variable_name === name)?.item_id ?? null;
    cache.set(name, id); env.globals[key] = cache;
    return id;
}
